import { useEffect, useState, useCallback } from "react";
import { api, formatApiError, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Plus, Trash2, Link2, Loader2, Camera, Video, Download, Mail, GripVertical, Upload, Copy, Image as ImageIcon, Search, Info, Users, ListPlus, Send, CheckSquare, Square } from "lucide-react";

const STATUS_OPTIONS = [
  { v: "pre_order", l: "Pre-Order" },
  { v: "pre_release", l: "Pre-Release" },
  { v: "instant_download", l: "Instant Download" },
  { v: "closed", l: "Closed" },
  { v: "expired", l: "Expired" },
];
const TYPE_OPTIONS = [
  { v: "photos", l: "Photos", icon: Camera },
  { v: "video", l: "Video", icon: Video },
  { v: "download", l: "Download", icon: Download },
];
const DELIVERY_OPTIONS = [
  { v: "undelivered", l: "Undelivered" },
  { v: "pending", l: "Pending" },
  { v: "delivered", l: "Delivered" },
];

export default function AdminMedia() {
  const [items, setItems] = useState(null);
  const [events, setEvents] = useState([]);
  const [selected, setSelected] = useState([]);
  const [linksFor, setLinksFor] = useState(null);
  const [detailsFor, setDetailsFor] = useState(null);
  const [recipientsFor, setRecipientsFor] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [saving, setSaving] = useState({});
  const [filters, setFilters] = useState({ event_id: "", delivery_status: "", payment_status: "", q: "" });

  const load = useCallback(() => {
    const params = {};
    if (filters.event_id) params.event_id = filters.event_id;
    if (filters.delivery_status) params.delivery_status = filters.delivery_status;
    if (filters.payment_status) params.payment_status = filters.payment_status;
    if (filters.q) params.q = filters.q;
    api.get("/media", { params }).then(({ data }) => setItems(data)).catch(() => setItems([]));
  }, [filters]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/admin/events").then(({ data }) => setEvents(data)).catch(() => {}); }, []);

  const patch = async (id, changes, optimistic = true) => {
    if (optimistic) setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...changes } : it)));
    setSaving((s) => ({ ...s, [id]: true }));
    try {
      const { data } = await api.put(`/media/${id}`, changes);
      setItems((prev) => prev.map((it) => (it.id === id ? data : it)));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      load();
    } finally {
      setSaving((s) => ({ ...s, [id]: false }));
    }
  };

  const addRow = async () => {
    try {
      const { data } = await api.post("/media", { title: "Untitled media", for_sale: true, order_status: "instant_download", event_id: filters.event_id || null });
      setItems((prev) => [data, ...prev]);
      toast.success("Row added");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const removeRow = async (id) => {
    setItems((prev) => prev.filter((it) => it.id !== id));
    setSelected((s) => s.filter((x) => x !== id));
    try {
      await api.delete(`/media/${id}`);
    } catch {
      load();
    }
  };

  const toggleType = (item, t) => {
    if (item.media_types.includes(t)) return;
    patch(item.id, { media_types: [t] });
  };

  const bulkStatus = async (status) => {
    try {
      await api.post("/media/bulk-update", { ids: selected, changes: { order_status: status } });
      toast.success(`Updated ${selected.length} item(s)`);
      setSelected([]);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const bulkDelete = async () => {
    try {
      await api.post("/media/bulk-delete", { ids: selected });
      toast.success(`Deleted ${selected.length} item(s)`);
      setSelected([]);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  if (items === null) {
    return <div className="flex justify-center py-32"><Loader2 className="h-8 w-8 animate-spin text-[#ff2a5f]" /></div>;
  }

  const allSelected = items.length > 0 && selected.length === items.length;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="font-mono-x text-xs uppercase tracking-widest text-zinc-500">Media Library</div>
          <h1 className="font-display text-2xl font-extrabold uppercase tracking-tight text-white sm:text-3xl">
            Manage all media
          </h1>
        </div>
        <div className="flex gap-2">
          <Button data-testid="bulk-create-open" onClick={() => setBulkOpen(true)} variant="outline" className="border-zinc-700 bg-transparent text-white hover:bg-zinc-800">
            <ListPlus className="mr-1.5 h-4 w-4" /> Bulk create
          </Button>
          <Button data-testid="add-media-row" onClick={addRow} className="bg-[#ff2a5f] font-semibold text-white hover:bg-[#e01f50]">
            <Plus className="mr-1.5 h-4 w-4" /> New media
          </Button>
        </div>
      </div>

      {/* Filter toolbar */}
      <div className="flex flex-wrap items-center gap-2 rounded-xl border border-zinc-800 bg-[#0d0d10] p-3">
        <div className="relative min-w-[200px] flex-1">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-zinc-500" />
          <Input data-testid="media-search" value={filters.q} onChange={(e) => setFilters((s) => ({ ...s, q: e.target.value }))} placeholder="Search title, dancer, studio, class, number…" className="h-9 border-zinc-700 bg-zinc-950 pl-9 text-sm text-white" />
        </div>
        <Select value={filters.event_id || "all"} onValueChange={(v) => setFilters((s) => ({ ...s, event_id: v === "all" ? "" : v }))}>
          <SelectTrigger data-testid="filter-event" className="h-9 w-48 border-zinc-700 bg-zinc-950 text-sm text-white"><SelectValue placeholder="All events" /></SelectTrigger>
          <SelectContent className="border-zinc-800 bg-zinc-900 text-white">
            <SelectItem value="all">All events</SelectItem>
            {events.map((ev) => <SelectItem key={ev.id} value={ev.id}>{ev.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.payment_status || "all"} onValueChange={(v) => setFilters((s) => ({ ...s, payment_status: v === "all" ? "" : v }))}>
          <SelectTrigger data-testid="filter-payment" className="h-9 w-40 border-zinc-700 bg-zinc-950 text-sm text-white"><SelectValue placeholder="Any payment" /></SelectTrigger>
          <SelectContent className="border-zinc-800 bg-zinc-900 text-white">
            <SelectItem value="all">Any payment</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
            <SelectItem value="unpaid">Unpaid</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filters.delivery_status || "all"} onValueChange={(v) => setFilters((s) => ({ ...s, delivery_status: v === "all" ? "" : v }))}>
          <SelectTrigger data-testid="filter-delivery" className="h-9 w-40 border-zinc-700 bg-zinc-950 text-sm text-white"><SelectValue placeholder="Any delivery" /></SelectTrigger>
          <SelectContent className="border-zinc-800 bg-zinc-900 text-white">
            <SelectItem value="all">Any delivery</SelectItem>
            {DELIVERY_OPTIONS.map((d) => <SelectItem key={d.v} value={d.v}>{d.l}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Bulk actions bar */}
      {selected.length > 0 && (
        <div data-testid="bulk-bar" className="flex flex-wrap items-center gap-3 rounded-lg border border-[#ff2a5f]/40 bg-[#ff2a5f]/10 px-4 py-3">
          <span className="text-sm font-medium text-white">{selected.length} selected</span>
          <Select onValueChange={bulkStatus}>
            <SelectTrigger data-testid="bulk-status" className="h-8 w-48 border-zinc-700 bg-zinc-950 text-sm text-white">
              <SelectValue placeholder="Set status…" />
            </SelectTrigger>
            <SelectContent className="border-zinc-800 bg-zinc-900 text-white">
              {STATUS_OPTIONS.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button data-testid="bulk-delete" onClick={bulkDelete} variant="outline" className="h-8 border-rose-500/40 bg-transparent text-rose-400 hover:bg-rose-500/10">
            <Trash2 className="mr-1.5 h-4 w-4" /> Delete
          </Button>
        </div>
      )}

      {/* ClickUp-style table */}
      <div className="w-full overflow-x-auto rounded-xl border border-zinc-800 bg-[#0d0d10] shadow-2xl">
        <table className="w-full min-w-[1600px] border-collapse text-sm">
          <thead>
            <tr className="border-b border-zinc-800 bg-zinc-900/80 text-left font-mono-x text-[11px] uppercase tracking-wider text-zinc-500">
              <th className="w-10 px-3 py-3">
                <Checkbox
                  data-testid="select-all"
                  checked={allSelected}
                  onCheckedChange={(c) => setSelected(c ? items.map((i) => i.id) : [])}
                />
              </th>
              <th className="min-w-[240px] px-3 py-3">Title</th>
              <th className="min-w-[160px] px-3 py-3">Event</th>
              <th className="min-w-[180px] px-3 py-3">Media Type</th>
              <th className="w-28 px-3 py-3">Price</th>
              <th className="w-40 px-3 py-3">Release</th>
              <th className="w-40 px-3 py-3">Expiry</th>
              <th className="w-44 px-3 py-3">Availability status</th>
              <th className="w-20 px-3 py-3">Sale</th>
              <th className="w-24 px-3 py-3">Send Email</th>
              <th className="w-28 px-3 py-3">Published</th>
              <th className="w-36 px-3 py-3">Delivery</th>
              <th className="w-24 px-3 py-3">Links</th>
              <th className="w-14 px-3 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id} data-testid={`clickup-row-${item.id}`} className="group border-b border-zinc-800/60 hover:bg-zinc-900/40">
                <td className="px-3 py-2">
                  <Checkbox
                    data-testid={`row-select-${item.id}`}
                    checked={selected.includes(item.id)}
                    onCheckedChange={(c) => setSelected((s) => (c ? [...s, item.id] : s.filter((x) => x !== item.id)))}
                  />
                </td>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-1.5">
                    <GripVertical className="h-3.5 w-3.5 shrink-0 text-zinc-700" />
                    <input
                      data-testid={`row-title-${item.id}`}
                      defaultValue={item.title}
                      onBlur={(e) => e.target.value !== item.title && patch(item.id, { title: e.target.value })}
                      className="w-full rounded border border-transparent bg-transparent px-2 py-1 font-medium text-white outline-none focus:border-zinc-700 focus:bg-zinc-950"
                    />
                  </div>
                </td>
                <td className="px-3 py-2">
                  <Select value={item.event_id || "none"} onValueChange={(v) => patch(item.id, { event_id: v === "none" ? null : v })}>
                    <SelectTrigger data-testid={`row-event-${item.id}`} className="h-8 border-zinc-800 bg-zinc-950 text-xs text-white"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent className="border-zinc-800 bg-zinc-900 text-white">
                      <SelectItem value="none">— None —</SelectItem>
                      {events.map((ev) => <SelectItem key={ev.id} value={ev.id}>{ev.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </td>
                <td className="px-3 py-2">
                  <div className="flex gap-1">
                    {TYPE_OPTIONS.map((t) => {
                      const Icon = t.icon;
                      const active = item.media_types.includes(t.v);
                      return (
                        <button
                          key={t.v}
                          data-testid={`row-type-${item.id}-${t.v}`}
                          title={t.l}
                          onClick={() => toggleType(item, t.v)}
                          className={`flex h-7 w-7 items-center justify-center rounded border transition-colors ${
                            active ? "border-[#ff2a5f] bg-[#ff2a5f]/15 text-[#ff2a5f]" : "border-zinc-700 text-zinc-600 hover:text-zinc-300"
                          }`}
                        >
                          <Icon className="h-3.5 w-3.5" />
                        </button>
                      );
                    })}
                  </div>
                </td>
                <td className="px-3 py-2">
                  <input
                    data-testid={`row-price-${item.id}`}
                    type="number"
                    step="0.01"
                    defaultValue={item.price}
                    onBlur={(e) => Number(e.target.value) !== item.price && patch(item.id, { price: Number(e.target.value) })}
                    className="w-full rounded border border-transparent bg-transparent px-2 py-1 text-white outline-none focus:border-zinc-700 focus:bg-zinc-950"
                  />
                </td>
                <td className="px-3 py-2">
                  <input
                    data-testid={`row-release-${item.id}`}
                    type="date"
                    defaultValue={item.release_date || ""}
                    onChange={(e) => patch(item.id, { release_date: e.target.value || null })}
                    className="w-full rounded border border-zinc-800 bg-zinc-950 px-2 py-1 text-xs text-zinc-300 outline-none focus:border-zinc-600 [color-scheme:dark]"
                  />
                </td>
                <td className="px-3 py-2">
                  <input
                    data-testid={`row-expiry-${item.id}`}
                    type="date"
                    defaultValue={item.expiry_date || ""}
                    onChange={(e) => patch(item.id, { expiry_date: e.target.value || null })}
                    className="w-full rounded border border-zinc-800 bg-zinc-950 px-2 py-1 text-xs text-zinc-300 outline-none focus:border-zinc-600 [color-scheme:dark]"
                  />
                </td>
                <td className="px-3 py-2">
                  <Select value={item.order_status} onValueChange={(v) => patch(item.id, { order_status: v })}>
                    <SelectTrigger data-testid={`row-status-${item.id}`} className="h-8 border-zinc-800 bg-zinc-950 text-xs text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="border-zinc-800 bg-zinc-900 text-white">
                      {STATUS_OPTIONS.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </td>
                <td className="px-3 py-2">
                  <Switch data-testid={`row-sale-${item.id}`} checked={item.for_sale} onCheckedChange={(c) => patch(item.id, { for_sale: c })} />
                </td>
                <td className="px-3 py-2">
                  <Checkbox data-testid={`row-email-${item.id}`} checked={item.send_email} onCheckedChange={(c) => patch(item.id, { send_email: !!c })} />
                </td>
                <td className="px-3 py-2">
                  <Switch data-testid={`row-published-${item.id}`} checked={!!item.published_in_product} onCheckedChange={(c) => patch(item.id, { published_in_product: c })} />
                </td>
                <td className="px-3 py-2">
                  <Select value={item.delivery_status || "undelivered"} onValueChange={(v) => patch(item.id, { delivery_status: v })}>
                    <SelectTrigger data-testid={`row-delivery-${item.id}`} className="h-8 border-zinc-800 bg-zinc-950 text-xs text-white"><SelectValue /></SelectTrigger>
                    <SelectContent className="border-zinc-800 bg-zinc-900 text-white">
                      {DELIVERY_OPTIONS.map((d) => <SelectItem key={d.v} value={d.v}>{d.l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </td>
                <td className="px-3 py-2">
                  <button
                    data-testid={`row-links-${item.id}`}
                    onClick={() => setLinksFor(item)}
                    className="inline-flex items-center gap-1 rounded border border-zinc-700 px-2 py-1 text-xs text-zinc-300 hover:border-cyan-500/50 hover:text-cyan-400"
                  >
                    <Link2 className="h-3.5 w-3.5" /> {item.links?.length || 0}
                  </button>
                </td>
                <td className="px-3 py-2 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <button data-testid={`row-recipients-${item.id}`} onClick={() => setRecipientsFor(item)} className="text-zinc-500 hover:text-white" title="Recipients & delivery"><Users className="h-4 w-4" /></button>
                    <button data-testid={`row-details-${item.id}`} onClick={() => setDetailsFor(item)} className="text-zinc-500 hover:text-white" title="Performer details"><Info className="h-4 w-4" /></button>
                    {saving[item.id] ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin text-zinc-500" />
                    ) : (
                      <button
                        data-testid={`row-delete-${item.id}`}
                        onClick={() => removeRow(item.id)}
                        className="text-zinc-600 opacity-0 transition-opacity hover:text-rose-400 group-hover:opacity-100"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            <tr>
              <td colSpan={14} className="px-3 py-2">
                <button
                  data-testid="quick-add-row"
                  onClick={addRow}
                  className="flex w-full items-center gap-2 rounded px-2 py-2 text-sm text-zinc-500 hover:bg-zinc-900/60 hover:text-white"
                >
                  <Plus className="h-4 w-4" /> Add media
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <LinksDialog item={linksFor} onClose={() => setLinksFor(null)} onSave={(payload) => { patch(linksFor.id, payload); setLinksFor(null); }} />
      {detailsFor && <DetailsDialog item={detailsFor} events={events} onClose={() => setDetailsFor(null)} onSave={(changes) => { patch(detailsFor.id, changes); setDetailsFor(null); }} />}
      {recipientsFor && <RecipientsDialog item={recipientsFor} onClose={() => setRecipientsFor(null)} />}
      {bulkOpen && <BulkCreateDialog events={events} onClose={() => setBulkOpen(false)} onDone={() => { setBulkOpen(false); load(); }} />}
    </div>
  );
}

function LinksDialog({ item, onClose, onSave }) {
  const [links, setLinks] = useState([]);
  const [thumb, setThumb] = useState("");
  const [uploading, setUploading] = useState(null);
  useEffect(() => {
    setLinks(item ? (item.links || []).map((l) => ({ ...l })) : []);
    setThumb(item ? item.thumbnail_url || "" : "");
  }, [item]);
  if (!item) return null;

  const update = (i, field, val) => setLinks((ls) => ls.map((l, idx) => (idx === i ? { ...l, [field]: val } : l)));
  const add = () => setLinks((ls) => [...ls, { id: crypto.randomUUID(), label: "Download link", url: "", password: "" }]);
  const remove = (i) => setLinks((ls) => ls.filter((_, idx) => idx !== i));

  const doUpload = async (file, target) => {
    if (!file) return;
    setUploading(target);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd);
      const url = fileUrl(data.path);
      if (target === "thumb") setThumb(url);
      else update(target, "url", url);
      toast.success("File uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(null);
    }
  };

  const copyShare = async (l) => {
    const url = `${window.location.origin}/share/${item.id}/${l.id}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Share link copied — anyone with it can access using the password");
    } catch {
      window.prompt("Copy this share link:", url);
    }
  };

  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto border-zinc-800 bg-zinc-900 text-white sm:max-w-2xl">
        <DialogHeader><DialogTitle className="font-display">Assets — {item.title}</DialogTitle></DialogHeader>

        <div className="flex items-center gap-4 rounded-lg border border-zinc-800 bg-zinc-950 p-3">
          <div className="h-16 w-24 shrink-0 overflow-hidden rounded bg-zinc-900">
            {thumb ? (
              <img src={thumb} alt="cover" className="h-full w-full object-cover" />
            ) : (
              <div className="flex h-full items-center justify-center text-zinc-700"><ImageIcon className="h-5 w-5" /></div>
            )}
          </div>
          <div className="flex-1">
            <Label className="text-[10px] uppercase text-zinc-500">Cover image</Label>
            <Input data-testid="thumb-url" value={thumb} onChange={(e) => setThumb(e.target.value)} placeholder="Paste URL or upload" className="mt-1 h-8 border-zinc-700 bg-zinc-900 text-sm text-white" />
          </div>
          <label className="cursor-pointer">
            <input type="file" accept="image/*" className="hidden" data-testid="thumb-upload" onChange={(e) => doUpload(e.target.files?.[0], "thumb")} />
            <span className="inline-flex items-center gap-1.5 rounded border border-zinc-700 px-3 py-2 text-xs text-zinc-300 hover:border-[#ff2a5f]/50 hover:text-white">
              {uploading === "thumb" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Upload
            </span>
          </label>
        </div>

        <div className="space-y-3">
          <p className="text-xs text-zinc-500">Multiple links can belong to one media item. Add a password to protect a link, then use "Share link" to hand out a public password page — perfect for one link shared with a whole class.</p>
          {links.map((l, i) => (
            <div key={l.id} data-testid={`link-editor-${i}`} className="space-y-2 rounded-lg border border-zinc-800 bg-zinc-950 p-3">
              <div className="grid grid-cols-12 gap-2">
                <div className="col-span-4">
                  <Label className="text-[10px] uppercase text-zinc-500">Label</Label>
                  <Input data-testid={`link-label-${i}`} value={l.label} onChange={(e) => update(i, "label", e.target.value)} className="mt-1 h-8 border-zinc-700 bg-zinc-900 text-sm text-white" />
                </div>
                <div className="col-span-5">
                  <Label className="text-[10px] uppercase text-zinc-500">URL</Label>
                  <Input data-testid={`link-url-${i}`} value={l.url} onChange={(e) => update(i, "url", e.target.value)} placeholder="https://…" className="mt-1 h-8 border-zinc-700 bg-zinc-900 text-sm text-white" />
                </div>
                <div className="col-span-3">
                  <Label className="text-[10px] uppercase text-zinc-500">Password</Label>
                  <Input data-testid={`link-password-${i}`} value={l.password} onChange={(e) => update(i, "password", e.target.value)} placeholder="optional" className="mt-1 h-8 border-zinc-700 bg-zinc-900 text-sm text-white" />
                </div>
              </div>
              <div className="flex items-center justify-between">
                <label className="cursor-pointer">
                  <input type="file" className="hidden" data-testid={`link-upload-${i}`} onChange={(e) => doUpload(e.target.files?.[0], i)} />
                  <span className="inline-flex items-center gap-1.5 text-xs text-zinc-400 hover:text-white">
                    {uploading === i ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Upload file
                  </span>
                </label>
                <div className="flex items-center gap-4">
                  <button type="button" data-testid={`link-share-${i}`} onClick={() => copyShare(l)} className="inline-flex items-center gap-1.5 text-xs text-cyan-400 hover:text-cyan-300">
                    <Copy className="h-3.5 w-3.5" /> Share link
                  </button>
                  <button data-testid={`link-remove-${i}`} onClick={() => remove(i)} className="text-zinc-600 hover:text-rose-400"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            </div>
          ))}
          <Button data-testid="add-link" variant="outline" onClick={add} className="w-full border-dashed border-zinc-700 bg-transparent text-zinc-400 hover:text-white">
            <Plus className="mr-1.5 h-4 w-4" /> Add link
          </Button>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="text-zinc-400">Cancel</Button>
          <Button data-testid="save-links" onClick={() => onSave({ links, thumbnail_url: thumb || null })} className="bg-[#ff2a5f] text-white hover:bg-[#e01f50]">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function DetailsDialog({ item, events, onClose, onSave }) {
  const ev = events.find((e) => e.id === item.event_id);
  const fields = ev ? { name: ev.field_name, number: ev.field_number, studio: ev.field_studio, class: ev.field_class } : null;
  const [d, setD] = useState({
    performer_name: item.performer_name || "",
    competitor_number: item.competitor_number || "",
    studio: item.studio || "",
    class_name: item.class_name || "",
    access_mode: item.access_mode || "single",
  });
  const set = (k, v) => setD((s) => ({ ...s, [k]: v }));
  const lbl = (key, fallback) => (fields && fields[key]?.label ? fields[key].label : fallback);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="border-zinc-800 bg-zinc-900 text-white sm:max-w-md">
        <DialogHeader><DialogTitle className="font-display">Details — {item.title}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {!ev && <p className="text-xs text-amber-400/80">Attach this media to a product to control which of these fields are used and their labels.</p>}
          <div><Label className="text-zinc-300">{lbl("name", "Dancer / team name")}</Label><Input data-testid="detail-name" value={d.performer_name} onChange={(e) => set("performer_name", e.target.value)} className="mt-1 border-zinc-700 bg-zinc-950 text-white" /></div>
          <div><Label className="text-zinc-300">{lbl("number", "Competitor number")}</Label><Input data-testid="detail-number" value={d.competitor_number} onChange={(e) => set("competitor_number", e.target.value)} className="mt-1 border-zinc-700 bg-zinc-950 text-white" /></div>
          <div><Label className="text-zinc-300">{lbl("studio", "Studio")}</Label><Input data-testid="detail-studio" value={d.studio} onChange={(e) => set("studio", e.target.value)} className="mt-1 border-zinc-700 bg-zinc-950 text-white" /></div>
          <div><Label className="text-zinc-300">{lbl("class", "Class")}</Label><Input data-testid="detail-class" value={d.class_name} onChange={(e) => set("class_name", e.target.value)} className="mt-1 border-zinc-700 bg-zinc-950 text-white" /></div>
          <div>
            <Label className="text-zinc-300">Access mode</Label>
            <Select value={d.access_mode} onValueChange={(v) => set("access_mode", v)}>
              <SelectTrigger data-testid="detail-access" className="mt-1 border-zinc-700 bg-zinc-950 text-white"><SelectValue /></SelectTrigger>
              <SelectContent className="border-zinc-800 bg-zinc-900 text-white">
                <SelectItem value="single">Single access (one email)</SelectItem>
                <SelectItem value="multi">Multi access (many emails)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="text-zinc-400">Cancel</Button>
          <Button data-testid="save-details" onClick={() => onSave(d)} className="bg-[#ff2a5f] text-white hover:bg-[#e01f50]">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RecipientsDialog({ item, onClose }) {
  const [recs, setRecs] = useState(null);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/media/${item.id}/recipients`).then(({ data }) => setRecs(data)).catch(() => setRecs([]));
  useEffect(() => { load(); }, []);

  const add = async () => {
    setBusy(true);
    try {
      await api.post(`/media/${item.id}/recipients`, { email, name });
      toast.success("Recipient added");
      setEmail(""); setName(""); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const resend = async (d) => {
    try { await api.post(`/media/${item.id}/recipients/${d.id}/resend`); toast.success("Download email sent"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (d) => { await api.delete(`/media/${item.id}/recipients/${d.id}`); load(); };

  const paid = (recs || []).filter((r) => r.paid);
  const unpaid = (recs || []).filter((r) => !r.paid);

  const Row = ({ d }) => (
    <div data-testid={`recipient-${d.id}`} className="flex items-center justify-between rounded-lg border border-zinc-800 bg-zinc-950 px-3 py-2">
      <div className="flex items-center gap-2">
        {d.email_sent ? <CheckSquare className="h-4 w-4 text-emerald-400" /> : <Square className="h-4 w-4 text-zinc-600" />}
        <span className="text-sm text-white">{d.email}</span>
        <span className="text-xs text-zinc-600">{d.email_sent ? "sent" : "not sent"}</span>
      </div>
      <div className="flex items-center gap-2">
        <Button size="sm" disabled={!d.paid} onClick={() => resend(d)} data-testid={`resend-${d.id}`} className="h-7 bg-zinc-800 text-white hover:bg-zinc-700 disabled:opacity-40" title={d.paid ? "" : "Order not paid"}>
          <Send className="mr-1 h-3.5 w-3.5" />{d.email_sent ? "Resend" : "Send"}
        </Button>
        <button data-testid={`remove-recipient-${d.id}`} onClick={() => remove(d)} className="text-zinc-600 hover:text-rose-400"><Trash2 className="h-4 w-4" /></button>
      </div>
    </div>
  );

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto border-zinc-800 bg-zinc-900 text-white sm:max-w-lg">
        <DialogHeader><DialogTitle className="font-display">Recipients — {item.title}</DialogTitle></DialogHeader>
        <p className="text-xs text-zinc-500">Access mode: <span className="text-zinc-300">{item.access_mode === "multi" ? "Multi (many emails)" : "Single (one email)"}</span>. Paid recipients can be sent their download link; on-hold recipients move to Paid once their order is released.</p>
        {recs === null ? (
          <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-[#ff2a5f]" /></div>
        ) : (
          <div className="space-y-4">
            <div>
              <div className="mb-1 font-mono-x text-[11px] uppercase tracking-widest text-emerald-400">Paid ({paid.length})</div>
              <div className="space-y-2">{paid.length ? paid.map((d) => <Row key={d.id} d={d} />) : <p className="text-xs text-zinc-600">No paid recipients yet.</p>}</div>
            </div>
            <div>
              <div className="mb-1 font-mono-x text-[11px] uppercase tracking-widest text-amber-400">On hold ({unpaid.length})</div>
              <div className="space-y-2">{unpaid.length ? unpaid.map((d) => <Row key={d.id} d={d} />) : <p className="text-xs text-zinc-600">None on hold.</p>}</div>
            </div>
            <div className="space-y-2 rounded-lg border border-dashed border-zinc-700 p-3">
              <div className="font-mono-x text-[11px] uppercase tracking-widest text-zinc-500">Add recipient (no order)</div>
              <div className="flex gap-2">
                <Input data-testid="recipient-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email@example.com" className="h-8 border-zinc-700 bg-zinc-950 text-sm text-white" />
                <Input data-testid="recipient-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Name (optional)" className="h-8 border-zinc-700 bg-zinc-950 text-sm text-white" />
                <Button data-testid="add-recipient" onClick={add} disabled={busy} className="h-8 shrink-0 bg-[#ff2a5f] text-white hover:bg-[#e01f50]">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Add"}</Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function BulkCreateDialog({ events, onClose, onDone }) {
  const [eventId, setEventId] = useState("none");
  const [lineField, setLineField] = useState("title");
  const [lines, setLines] = useState("");
  const [price, setPrice] = useState(0);
  const [types, setTypes] = useState([]);
  const [status, setStatus] = useState("instant_download");
  const [access, setAccess] = useState("single");
  const [deliver, setDeliver] = useState(false);
  const [busy, setBusy] = useState(false);
  const toggle = (t) => setTypes((ts) => (ts.includes(t) ? ts.filter((x) => x !== t) : [...ts, t]));

  const submit = async () => {
    const arr = lines.split("\n").map((s) => s.trim()).filter(Boolean);
    if (!arr.length) return toast.error("Add at least one line");
    setBusy(true);
    try {
      const { data } = await api.post("/media/bulk-create", {
        event_id: eventId === "none" ? null : eventId,
        line_field: lineField,
        lines: arr,
        defaults: { price: Number(price), media_types: types, order_status: status, access_mode: access, send_email: deliver },
      });
      toast.success(`Created ${data.created} media`);
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[88vh] overflow-y-auto border-zinc-800 bg-zinc-900 text-white sm:max-w-lg">
        <DialogHeader><DialogTitle className="font-display">Bulk create media</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-zinc-300">Attach to product</Label>
              <Select value={eventId} onValueChange={setEventId}>
                <SelectTrigger data-testid="bulk-event" className="mt-1 border-zinc-700 bg-zinc-950 text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="border-zinc-800 bg-zinc-900 text-white">
                  <SelectItem value="none">— None —</SelectItem>
                  {events.map((ev) => <SelectItem key={ev.id} value={ev.id}>{ev.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-zinc-300">Each line is a…</Label>
              <Select value={lineField} onValueChange={setLineField}>
                <SelectTrigger data-testid="bulk-line-field" className="mt-1 border-zinc-700 bg-zinc-950 text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="border-zinc-800 bg-zinc-900 text-white">
                  <SelectItem value="title">Media title</SelectItem>
                  <SelectItem value="performer_name">Dancer / team name</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-zinc-300">One entry per line</Label>
            <textarea data-testid="bulk-lines" value={lines} onChange={(e) => setLines(e.target.value)} rows={6} placeholder={"Ava Smith\nBella Jones\nTeam Phoenix"} className="mt-1 w-full rounded-md border border-zinc-700 bg-zinc-950 p-2 text-sm text-white outline-none focus:border-zinc-500" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-zinc-300">Price (each)</Label>
              <Input data-testid="bulk-price" type="number" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} className="mt-1 border-zinc-700 bg-zinc-950 text-white" />
            </div>
            <div>
              <Label className="text-zinc-300">Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger data-testid="bulk-status" className="mt-1 border-zinc-700 bg-zinc-950 text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="border-zinc-800 bg-zinc-900 text-white">{STATUS_OPTIONS.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs uppercase text-zinc-500">Media types:</span>
            {TYPE_OPTIONS.map((t) => {
              const Icon = t.icon; const active = types.includes(t.v);
              return <button key={t.v} data-testid={`bulk-type-${t.v}`} onClick={() => toggle(t.v)} className={`flex h-8 w-8 items-center justify-center rounded border ${active ? "border-[#ff2a5f] bg-[#ff2a5f]/15 text-[#ff2a5f]" : "border-zinc-700 text-zinc-600"}`}><Icon className="h-3.5 w-3.5" /></button>;
            })}
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-xs uppercase text-zinc-500">Access</span>
              <Select value={access} onValueChange={setAccess}>
                <SelectTrigger data-testid="bulk-access" className="h-8 w-40 border-zinc-700 bg-zinc-950 text-sm text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="border-zinc-800 bg-zinc-900 text-white">
                  <SelectItem value="single">Single access</SelectItem>
                  <SelectItem value="multi">Multi access</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-2 text-sm text-zinc-300">
              <Checkbox data-testid="bulk-deliver" checked={deliver} onCheckedChange={(c) => setDeliver(!!c)} /> Deliver email
            </label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="text-zinc-400">Cancel</Button>
          <Button data-testid="bulk-submit" onClick={submit} disabled={busy} className="bg-[#ff2a5f] text-white hover:bg-[#e01f50]">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create media"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

